// React imports
import React, { ChangeEvent, useEffect, useState } from 'react';

// Router imports
import { useNavigate } from 'react-router-dom';

// Redux imports
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// Asset imports
import AddUrl from '../assets/plus.png';
import LeftArrow from '../assets/left-arrow.png';
import RemoveUrl from '../assets/cross.png';
import Spark from '../assets/spark.png';
import SupportParnershipInfo from '../assets/questionMark.png';

// Component imports
import Button from './common/Button';
import GenericConfirmationModal from './common/GenericConfirmationModal';
import CustomSelect from './common/Select';
import Input from './common/Input';
import Label from './common/Label';
import Loading from './common/Loading';
import Text from './common/Text';

// Style imports
import '../styles/Idea.css';

// Constant imports
import { ADD_IDEA_MAX_LENGTHS, TENANT_ID } from '../constants/Constants';

// API imports
import { postIdea } from '../api/IdeaService';

// Utility imports
import { validatePatent } from '../utils/CommonUtils';

// Type imports
import {
  AddIdeaCharCounts,
  IdeaPayload,
  IPStatusesInfo,
  Partnership,
  PostIdeaResponse,
} from '../types/types';

// External library imports
import { MultiValue, SingleValue } from 'react-select';
import { Tooltip } from 'react-tooltip';

const CreateIdea: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const user = useSelector((state: RootState) => state.user.userDetails);
  const supportAndPartnershipPreferences = useSelector(
    (state: RootState) => state.support_or_partnership,
  );
  const ipStatusRedux = useSelector((state: RootState) => state.ip_statuses);
  const [patentPatternError, setPatentPatternError] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [showPatentLimitMessage, setShowPatentLimitMessage] = useState(false);
  const [addIdeaData, setAddIdeaData] = useState<IdeaPayload>({
    tenant_id: TENANT_ID,
    title: '',
    introduction: '',
    problem: '',
    opportunity: '',
    support_ids: [],
    urls: [{ url: '', title: '' }],
    ip_status_id: null,
    patents: [],
  });
  const [validationErrors, setValidationErrors] = useState<{
    title?: string;
    introduction?: string;
    [key: string]: string | undefined;
  }>({});
  const [addIdeaCharCounts, setAddIdeaCharCounts] = useState<AddIdeaCharCounts>(
    {
      title: 0,
      introduction: 0,
      problem: 0,
      opportunity: 0,
      supportingUrlTitle: [0],
    },
  );
  const [selectedSupports, setSelectedSupports] = useState<
    { id: number; name: string }[]
  >([]);
  const parsedSupportsOptions =
    supportAndPartnershipPreferences.support_or_partnership;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createdIdeaId, setCreatedIdeaId] = useState<number | null>(null);
  const parsedIpStatuses = ipStatusRedux.ip_statuses;
  const [selectedIP, setSelectedIP] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [patentNumberArray, setPatentNumberArray] = useState<string[]>(['']);
  const [patentNumberValuesArray, setPatentNumberValuesArray] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  const addSupportingUrl = () => {
    if (addIdeaData.urls.length < 3) {
      setAddIdeaData((prevData) => ({
        ...prevData,
        urls: [...prevData.urls, { url: '', title: '' }],
      }));
      setShowLimitMessage(false);
    } else {
      setShowLimitMessage(true);
      setTimeout(() => {
        setShowLimitMessage(false);
      }, 5000);
    }
  };

  const addPatentField = () => {
    if (patentNumberArray.length < 4) {
      setPatentNumberArray((prevData) => [...prevData, '']);
      setShowPatentLimitMessage(false);
    } else {
      setShowPatentLimitMessage(true);
      setTimeout(() => {
        setShowPatentLimitMessage(false);
      }, 5000);
    }
  };
  const removePatentField = (index: number) => {
    const updatedPatents = patentNumberArray.filter(
      (item, itemIndex) => itemIndex !== index,
    );
    setPatentNumberArray([...updatedPatents]);
    const updatedPatentNumberValuesArray = patentNumberValuesArray.filter(
      (item, itemIndex) => itemIndex !== index,
    );
    setPatentNumberValuesArray([...updatedPatentNumberValuesArray]);
  };
  const updatePatentNumber = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    index: number,
  ) => {
    const { value } = event.target;
    const updatedPatentNumberArray = [...patentNumberValuesArray];
    const testPatent = validatePatent(value);
    if (!testPatent && value) {
      const updatedErrors = [...patentPatternError];
      updatedErrors[index] = true;
      setPatentPatternError(updatedErrors);
    } else {
      const updatedErrors = [...patentPatternError];
      updatedErrors[index] = false;
      setPatentPatternError(updatedErrors);
    }
    updatedPatentNumberArray[index] = value;
    setPatentNumberValuesArray(updatedPatentNumberArray);
  };

  const validateRequiredFields = () => {
    const errors: { [key: string]: string } = {};

    if (!addIdeaData.title) {
      errors.title = 'Title is required';
    }
    if (!addIdeaData.introduction) {
      errors.introduction = 'Introduction is required';
    }

    addIdeaData.urls.forEach((urlSet, index) => {
      if ((urlSet.url && !urlSet.title) || (!urlSet.url && urlSet.title)) {
        errors[`urlSet${index}`] = 'URL and Title must be filled together';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateIdeaData = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    index?: number,
  ) => {
    const { name, value } = e.target;

    if (index !== undefined) {
      const updatedSupportingUrls = [...addIdeaData.urls];
      const fieldName = name as keyof (typeof updatedSupportingUrls)[0];

      if (fieldName === 'title') {
        const maxCharLength = ADD_IDEA_MAX_LENGTHS.supportingUrlTitle;
        const truncatedValue = value.slice(0, maxCharLength);
        updatedSupportingUrls[index] = {
          ...updatedSupportingUrls[index],
          [fieldName]: truncatedValue,
        };

        setAddIdeaCharCounts((prevCounts) => {
          const newCounts = [...prevCounts.supportingUrlTitle];
          newCounts[index] = truncatedValue.length;
          return {
            ...prevCounts,
            supportingUrlTitle: newCounts,
          };
        });
      } else {
        updatedSupportingUrls[index] = {
          ...updatedSupportingUrls[index],
          [fieldName]: value,
        };
      }

      setAddIdeaData((prevData) => ({
        ...prevData,
        urls: updatedSupportingUrls,
      }));
    } else {
      const fieldName = name as keyof AddIdeaCharCounts;
      const maxCharLength = ADD_IDEA_MAX_LENGTHS[fieldName];
      const truncatedValue = value.slice(0, maxCharLength);

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: '',
      }));

      setAddIdeaData((prevData) => ({
        ...prevData,
        [fieldName]: truncatedValue,
      }));

      setAddIdeaCharCounts((prevCounts) => ({
        ...prevCounts,
        [fieldName]: truncatedValue.length,
      }));
    }
  };

  const selectSupportsPartnership = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    if (Array.isArray(selectedOptions)) {
      const supports = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedSupports(supports);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedSupports(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedSupports([]);
    }
  };
  const selectIPStatus = (
    selectedIPOption:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    if (Array.isArray(selectedIPOption)) {
      const supports = selectedIPOption.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedIP(supports);
    } else if (selectedIPOption) {
      const singleOption = selectedIPOption as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedIP(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedIP([]);
    }
  };

  const removeSupportingUrl = (index: number) => {
    const updatedUrls = addIdeaData.urls.filter((_, i) => i !== index);
    const updatedCharCounts = addIdeaCharCounts.supportingUrlTitle.filter(
      (_, i) => i !== index,
    );

    setAddIdeaData((prevData) => ({
      ...prevData,
      urls: updatedUrls,
    }));

    setAddIdeaCharCounts((prevCounts) => ({
      ...prevCounts,
      supportingUrlTitle: updatedCharCounts,
    }));
  };

  const handlePostIdea = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    if (!validateRequiredFields()) {
      window.scrollTo(0, 0);
      setLoading(false);
      return;
    }
    const result = patentNumberValuesArray.filter(
      (item) => item !== '' && item !== undefined,
    );
    try {
      const filteredIdeaData = {
        ...addIdeaData,
        support_ids: selectedSupports.map((support) => support.id),
        urls: addIdeaData.urls.filter((urlSet) => urlSet.url && urlSet.title),
        ip_status_id: selectedIP.length > 0 ? selectedIP[0]['id'] : null,
        patents: result,
      };

      const finalPayload = {
        ...filteredIdeaData,
        ...(filteredIdeaData.problem
          ? { problem: filteredIdeaData.problem }
          : {}),
        ...(filteredIdeaData.opportunity
          ? { opportunity: filteredIdeaData.opportunity }
          : {}),
      };
      const response = await postIdea({
        ...finalPayload,
        user_id: user?.userId as number,
      });
      if ('code' in response) {
        console.error('Unexpected post idea response:', response);
        if (response.code === 'BAD_REQUEST') {
          const details = response.details;
          setValidationErrors((prev) => ({
            ...prev,
            apiError:
              details && details?.length > 0
                ? details[0].error + ' - ' + details[0].field
                : 'Unexpected error',
          }));
        } else if (response.code === 'UNAUTHORIZED') {
          setValidationErrors((prev) => ({
            ...prev,
            apiError: response.message,
          }));
        } else {
          setValidationErrors((prev) => ({
            ...prev,
            apiError: 'Unexpected error occurred. Please try again.',
          }));
        }
        setLoading(false);
      } else {
        setCreatedIdeaId((response as PostIdeaResponse).idea_id);
        setIsModalOpen(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error posting idea:', error);
      setLoading(false);
      setErrorMessage(`${error}`);
    }
  };

  const handleViewIdea = () => {
    setIsModalOpen(false);
    navigate(`/ideas/${createdIdeaId}`);
  };

  const handleGotoMyIdeas = () => {
    setIsModalOpen(false);
    navigate('/my-ideas');
  };

  return (
    <div className="common-container idea-padding-container custom-background">
      <form onSubmit={handlePostIdea} className="padding-0">
        <div className="grid gap-20">
          <div className="nav-bar">
            <Button
              type="button"
              className="nav-button"
              onClick={() => navigate(-1)}
              aria-label="Back to previous page"
            >
              <img src={LeftArrow} alt="Back" className="left-arrow-icon" />
            </Button>
            <div className="idea">
              <img src={Spark} alt="Spark Icon" className="spark-icon" />
              <div className="text-color-white small-medium ">Idea</div>
            </div>
          </div>
          <Text className="brand-title">Add Your Idea</Text>
          <div className="form-row">
            <Label htmlFor="title" className="input-label">
              Title<sup className="fs-12">*</sup>
            </Label>
            <Input
              type="text"
              name="title"
              value={addIdeaData.title}
              placeholder="Add your idea title"
              onChange={updateIdeaData}
              className="input-text"
            />
            <div className="field-error small-regular">
              {validationErrors.title && (
                <div className="error-message">
                  <p>{validationErrors.title}</p>
                </div>
              )}
              <Text className="small-regular count-label">
                {addIdeaCharCounts.title}/{ADD_IDEA_MAX_LENGTHS.title}
              </Text>
            </div>
          </div>
          <div className="form-row">
            <Label htmlFor="introduction" className="input-label">
              Introduction<sup className="fs-12">*</sup>
            </Label>
            <Input
              type="textarea"
              name="introduction"
              value={addIdeaData.introduction}
              placeholder="Enter a description introduction..."
              onChange={updateIdeaData}
              className="input-text large-textarea"
            />
            <div className="field-error small-regular">
              {validationErrors.introduction && (
                <div className="error-message">
                  <p>{validationErrors.introduction}</p>
                </div>
              )}
              <Text className="small-regular count-label">
                {addIdeaCharCounts.introduction}/
                {ADD_IDEA_MAX_LENGTHS.introduction}
              </Text>
            </div>
          </div>
          <div className="form-row">
            <Label htmlFor="problem" className="input-label">
              Problem
            </Label>
            <Input
              type="textarea"
              name="problem"
              value={addIdeaData.problem}
              placeholder="Describe the problem your idea will solve..."
              onChange={updateIdeaData}
              className="input-text large-textarea"
            />
            <Text className="small-regular count-label">
              {addIdeaCharCounts.problem}/{ADD_IDEA_MAX_LENGTHS.problem}
            </Text>
          </div>
          <div className="form-row">
            <Label htmlFor="opportunity" className="input-label">
              Opportunity
            </Label>
            <Input
              type="textarea"
              name="opportunity"
              value={addIdeaData.opportunity}
              placeholder="Describe the opportunity you seen in the market..."
              onChange={updateIdeaData}
              className="input-text large-textarea"
            />
            <Text className="small-regular count-label">
              {addIdeaCharCounts.opportunity}/{ADD_IDEA_MAX_LENGTHS.opportunity}
            </Text>
          </div>
          <div className="line"></div>
          <div className="form-row">
            <Label
              htmlFor="support-partnership"
              className="support-partnership-heading input-label"
            >
              Partnership or Support Needed
              <img
                src={SupportParnershipInfo}
                alt="info-icon"
                className="support-parnership-icon"
                id="partnership-tool-tip"
              />
              <Tooltip anchorSelect="#partnership-tool-tip" place="top-start">
                Partnership or Support Needed
              </Tooltip>
            </Label>
            <CustomSelect
              options={parsedSupportsOptions.map((option: Partnership) => ({
                label: option.name,
                value: option.id,
              }))}
              onChange={selectSupportsPartnership}
              value={selectedSupports.map((option) => ({
                label: option.name,
                value: option.id,
              }))}
              placeholder="Select what you are seeking"
              isMulti={true}
              isClearable={true}
              className="select-support-options"
            />
          </div>
          <div className="form-row">
            <Label
              htmlFor="ip-status"
              className="support-partnership-heading input-label"
            >
              IP Status
              <img
                src={SupportParnershipInfo}
                alt="info-icon"
                className="support-parnership-icon"
                id="ip-status-tool-tip"
              />
              <Tooltip anchorSelect="#ip-status-tool-tip" place="top-start">
                IP Status
              </Tooltip>
            </Label>
            <CustomSelect
              options={parsedIpStatuses.map((option: IPStatusesInfo) => ({
                label: option.name,
                value: option.id,
              }))}
              onChange={selectIPStatus}
              value={selectedIP.map((option) => ({
                label: option.name,
                value: option.id,
              }))}
              placeholder="Select current status of filing"
              isMulti={false}
              isClearable={true}
              className="select-support-options"
            />
            {patentNumberArray.map((item, index) => (
              <div key={index}>
                {index > 0 && (
                  <div className="d-flex justify-content-end">
                    <Button
                      type="button"
                      className="remove-url-button cross-margin"
                      onClick={() => removePatentField(index)}
                    >
                      <img
                        src={RemoveUrl}
                        alt="remove-button"
                        className="remove-url-icon"
                      />
                    </Button>
                  </div>
                )}
                <Input
                  type="text"
                  name="patent-number"
                  placeholder="Patent Number"
                  onChange={(event) => updatePatentNumber(event, index)}
                  className="input-text input-margin input-flex-styles"
                  value={patentNumberValuesArray[index] || ''}
                />
                {patentPatternError[index] ? (
                  <p className="error-message">
                    Please Enter Valid Patent Number
                  </p>
                ) : null}
              </div>
            ))}
            <Button
              className="add-url-section grid gap-6"
              type="button"
              onClick={addPatentField}
            >
              <img src={AddUrl} alt="+" className="add-url-button" />
              <Text className="add-url-heading input-label">
                Add Another Patent Number
              </Text>
            </Button>
            {showPatentLimitMessage && (
              <div className="error-message">
                <p>You can only add up to 4 Patent Numbers</p>
              </div>
            )}
          </div>
          <div className="line"></div>
          {addIdeaData.urls.map((urlSet, index) => (
            <div key={index} className="form-row">
              <div className="url-heading-remove-icon">
                <Label
                  htmlFor={`supportingUrl-${index}`}
                  className="input-label"
                >
                  Supporting URL
                </Label>
                {index > 0 && (
                  <Button
                    type="button"
                    className="remove-url-button"
                    onClick={() => removeSupportingUrl(index)}
                  >
                    <img
                      src={RemoveUrl}
                      alt="remove-button"
                      className="remove-url-icon"
                    />
                  </Button>
                )}
              </div>
              <Input
                type="text"
                name="url"
                value={urlSet.url}
                placeholder="https://www.example.com"
                onChange={(e) => {
                  updateIdeaData(e, index);
                  validationErrors[`urlSet${index}`] = '';
                  validationErrors[`apiError`] = '';
                }}
                className="input-text small-textarea"
              />
              <Input
                type="text"
                name="title"
                value={urlSet.title}
                placeholder="Supporting URL Title"
                onChange={(e) => {
                  updateIdeaData(e, index);
                  validationErrors[`urlSet${index}`] = '';
                  validationErrors[`apiError`] = '';
                }}
                className="input-text small-textarea"
              />
              <div className="field-error">
                {validationErrors[`urlSet${index}`] && (
                  <div className="error-message">
                    <p>{validationErrors[`urlSet${index}`]}</p>
                  </div>
                )}
                <Text className="small-regular count-label input-label">
                  {addIdeaCharCounts.supportingUrlTitle[index] || 0}/
                  {ADD_IDEA_MAX_LENGTHS.supportingUrlTitle}
                </Text>
              </div>
            </div>
          ))}
          <Button
            className="add-url-section grid gap-6"
            type="button"
            onClick={addSupportingUrl}
          >
            <img src={AddUrl} alt="+" className="add-url-button" />
            <Text className="add-url-heading input-label">
              Add Another supporting URL
            </Text>
          </Button>
          {showLimitMessage && (
            <div className="error-message">
              <p>You can only add up to 3 URLs</p>
            </div>
          )}
          {validationErrors[`apiError`] && (
            <div className="error-message">
              <p>{validationErrors[`apiError`]}</p>
            </div>
          )}
          {errorMessage && (
            <div>
              <Text className="error-message">{errorMessage}</Text>
            </div>
          )}
          <div className="line"></div>
          <div className="idea-action-button">
            <Button
              type="submit"
              className="tertiary-button"
              disabled={loading != null && loading}
            >
              {loading ? (
                <>
                  <Loading size={25} /> &nbsp; Post Your Idea
                </>
              ) : (
                'Post Your Idea'
              )}
            </Button>
          </div>
        </div>
      </form>
      <GenericConfirmationModal
        message="Thank you for your submission! Your idea has been successfully created."
        isOpen={isModalOpen}
        onFirstButtonAction={handleViewIdea}
        onFirstButtonName="View idea"
        onSecondButtonAction={handleGotoMyIdeas}
        onSecondButtonName="Go to My Ideas"
      />
    </div>
  );
};

export default CreateIdea;
